import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import CoverLayout from "layouts/authentication/components/CoverLayout";

import bgImage from "assets/images/home.jpg";
import { getAuthToken, setAuthToken } from "utils/authToken";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// These mirror schemas.UserCreate on the backend. Without them the form
// happily posts values Pydantic rejects, producing a 422 in the console on
// the first attempt of a signup that then succeeds on retry.
const USERNAME_RE = /^[A-Za-z0-9_.-]+$/;
const USERNAME_MIN = 3;
const USERNAME_MAX = 50;
const PASSWORD_MIN = 8;
const PASSWORD_MAX_BYTES = 72;

function validateUsername(value) {
  const v = value.trim();
  if (!v) return "Required";
  if (v.length < USERNAME_MIN) return `At least ${USERNAME_MIN} characters`;
  if (v.length > USERNAME_MAX) return `At most ${USERNAME_MAX} characters`;
  if (!USERNAME_RE.test(v)) return "Letters, numbers, dots, hyphens, and underscores only";
  return null;
}

function validatePassword(value) {
  if (value.length < PASSWORD_MIN) return `At least ${PASSWORD_MIN} characters`;
  if (new TextEncoder().encode(value).length > PASSWORD_MAX_BYTES) return "Password is too long";
  if (!/[a-zA-Z]/.test(value)) return "Must contain at least one letter";
  if (!/[0-9]/.test(value)) return "Must contain at least one digit";
  return null;
}

function Cover() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, content: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    if (token) navigate("/dashboard");
  }, [navigate]);

  const validate = () => {
    const e = {};
    const usernameError = validateUsername(username);
    if (usernameError) e.username = usernameError;
    if (!EMAIL_RE.test(email)) e.email = "Enter a valid email";
    const passwordError = validatePassword(password);
    if (passwordError) e.password = passwordError;
    if (!agreedToTerms) e.terms = "You must agree to the terms and conditions";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await apiClient.post(ENDPOINTS.auth.signup, {
        username: username.trim(),
        email: email.trim(),
        password,
      });
      setAuthToken(res.data.access_token);
      navigate("/onboarding");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((e) => e.msg).join(" ")
        : detail || "Sign-up failed.";
      setSnack({ open: true, content: message });
    }
  };

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox textAlign="center" mt={3}>
          <MDTypography variant="h4" fontWeight="medium">
            Become an Estían
          </MDTypography>
          <MDTypography display="block" variant="button" color="text" my={1}>
            Enter your email and password to register
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Username"
                variant="standard"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                error={!!errors.username}
                helperText={errors.username}
                inputProps={{ autoComplete: "off", name: "username" }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                variant="standard"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                variant="standard"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1} flexWrap="wrap">
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;I agree to the&nbsp;
              </MDTypography>
              <MDTypography
                component="a"
                href="/terms"
                variant="button"
                fontWeight="bold"
                color="info"
                textGradient
              >
                Terms of Service
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;and&nbsp;
              </MDTypography>
              <MDTypography
                component="a"
                href="/privacy"
                variant="button"
                fontWeight="bold"
                color="info"
                textGradient
              >
                Privacy Policy
              </MDTypography>
            </MDBox>
            {errors.terms && (
              <MDTypography variant="caption" color="error" display="block" ml={1} mt={0.5}>
                {errors.terms}
              </MDTypography>
            )}
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              ml={1}
              mt={1}
              sx={{ opacity: 0.85, lineHeight: 1.4 }}
            >
              Recipes and meal plans on Estìa are for general guidance only. Always use your own
              judgment, especially regarding allergies, dietary needs, and food safety. Estìa does
              not guarantee specific results.
            </MDTypography>
            <MDBox mt={4} mb={1}>
              <MDButton type="submit" variant="gradient" color="info" fullWidth>
                Sign Up
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign In
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <MDSnackbar
        color="error"
        icon="warning"
        title="Sign-up failed"
        dateTime=""
        content={snack.content}
        open={snack.open}
        close={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </CoverLayout>
  );
}

export default Cover;
